import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";

interface EmojiExplosionProps {
  emojis: string[];
  onComplete?: () => void;
}

export const EmojiExplosion = ({ emojis, onComplete }: EmojiExplosionProps) => {
  useEffect(() => {
    console.log("[EmojiExplosion] Component mounted with emojis:", emojis);
    
    // Call onComplete slightly before animation fully ends for smoother transition
    const timer = setTimeout(() => {
      console.log("[EmojiExplosion] Animation complete, triggering callback");
      onComplete?.();
    }, 1500); // Reduced from 2200 to trigger tutorial earlier

    return () => clearTimeout(timer);
  }, [emojis, onComplete]);

  // Generate particles with random trajectories - memoized to prevent jumping on re-renders
  const particles = useMemo(() => emojis.map((emoji, index) => {
    const angle = (Math.PI * 2 * index) / emojis.length + (Math.random() - 0.5) * 0.3;
    const distance = 200 + Math.random() * 150;
    const endX = Math.cos(angle) * distance;
    const endY = Math.sin(angle) * distance + 120; // Add gravity effect
    const rotation = (Math.random() - 0.5) * 360;
    
    return {
      id: index,
      emoji,
      endX,
      endY,
      rotation,
      delay: Math.random() * 100,
    };
  }), [emojis]);

  const explosionContent = (
    <div 
      className="fixed inset-0 pointer-events-none flex items-center justify-center"
      style={{ 
        zIndex: 99999,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        paddingBottom: '20vh', // Move center up
      }}
    >
      <style>{`
        @keyframes emoji-explode {
          0% {
            transform: translate(0, 0) rotate(0deg) scale(1);
            opacity: 1;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: translate(var(--end-x), var(--end-y)) rotate(var(--rotation)) scale(0.8);
            opacity: 0;
          }
        }
        
        .emoji-particle {
          position: absolute;
          font-size: 5rem;
          line-height: 1;
          user-select: none;
          pointer-events: none;
          will-change: transform, opacity;
        }
      `}</style>
      {particles.map((particle) => {
        console.log("[EmojiExplosion] Rendering particle:", particle.emoji, particle);
        return (
          <div
            key={particle.id}
            className="emoji-particle"
            style={{
              // @ts-ignore
              "--end-x": `${particle.endX}px`,
              "--end-y": `${particle.endY}px`,
              "--rotation": `${particle.rotation}deg`,
              animation: `emoji-explode 2s cubic-bezier(0.25, 0.1, 0.25, 1) forwards`,
              animationDelay: `${particle.delay}ms`,
            }}
          >
            {particle.emoji}
          </div>
        );
      })}
    </div>
  );

  console.log("[EmojiExplosion] Rendering to portal with", particles.length, "particles");
  // Render in a portal to ensure it stays visible even if parent component unmounts
  return createPortal(explosionContent, document.body);
};
